import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AnimatedLetters from '../AnimatedLetters'
import gsap from 'gsap'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { bio, skills, education, availability } = profile.about
const { location } = profile.contact
const currentJob = profile.experience.jobs.find((j) => j.isCurrent)

// Group rows under their category while preserving data order
const CATEGORIES = [...new Set(skills.map((s) => s.cat))]

const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const About = () => {
  const [letterClass, setLetterClass] = useState('text-animate')
  const pageRef = useRef(null)

  useEffect(() => {
    const t = setTimeout(() => setLetterClass('text-animate-hover'), 3000)
    return () => clearTimeout(t)
  }, [])

  // Count-up on the stack index numbers — one-shot, zero re-renders
  useEffect(() => {
    if (!pageRef.current) return
    const els = pageRef.current.querySelectorAll('.stack-row__pct')

    if (REDUCED_MOTION) {
      els.forEach((el) => {
        el.textContent = el.dataset.pct
      })
      return
    }

    const ctx = gsap.context(() => {
      els.forEach((el, i) => {
        const obj = { v: 0 }
        gsap.to(obj, {
          v: parseInt(el.dataset.pct, 10),
          duration: 1.1,
          delay: 0.7 + i * 0.07,
          ease: 'power3.out',
          onUpdate() {
            el.textContent = Math.round(obj.v)
          },
        })
      })
    }, pageRef)
    return () => ctx.revert()
  }, [])

  let row = 0

  return (
    <div className="about-page" ref={pageRef}>
      {/* ── Ghost word backdrop ── */}
      <span className="about-ghost" aria-hidden="true">
        Profile
      </span>

      {/* ── Left: the story ── */}
      <div className="about-story">
        <p className="about-kicker">
          <span className="about-kicker__rule" />
          Profile
        </p>

        <h1 className="about-title">
          <AnimatedLetters
            letterClass={letterClass}
            strArray={'Who I'.split('')}
            idx={12}
          />{' '}
          <em>
            <AnimatedLetters
              letterClass={letterClass}
              strArray={'am'.split('')}
              idx={18}
            />
          </em>
        </h1>

        <p className="about-bio">{bio}</p>

        <section className="about-edu">
          <h2 className="about-rule-title">
            <span className="about-rule-title__index">01</span>
            <span className="about-rule-title__text">Education</span>
            <span className="about-rule-title__line" />
          </h2>

          {education.map((edu, i) => (
            <article
              key={edu.degree}
              className="edu-chapter"
              style={{ '--i': i }}
            >
              <span className="edu-chapter__year">{edu.year}</span>
              <div className="edu-chapter__body">
                <p className="edu-chapter__degree">
                  {edu.degree}
                  {edu.stem && <span className="edu-chapter__stem">STEM</span>}
                </p>
                <p className="edu-chapter__school">
                  {edu.school} · {edu.location}
                </p>
              </div>
            </article>
          ))}
        </section>

        {currentJob && (
          <section className="about-now">
            <h2 className="about-rule-title">
              <span className="about-rule-title__index">02</span>
              <span className="about-rule-title__text">Currently</span>
              <span className="about-rule-title__line" />
            </h2>

            <Link
              to="/experience"
              className="now-card"
              data-cursor="career"
            >
              <div className="now-card__body">
                <p className="now-card__company">{currentJob.company}</p>
                <p className="now-card__role">
                  {currentJob.title}
                  <span className="now-card__sep">·</span>
                  <span className="now-card__range">{currentJob.range}</span>
                </p>
              </div>
              <span className="now-card__arrow" aria-hidden="true">
                ↗
              </span>
            </Link>
          </section>
        )}

        <p className="about-status">
          <span className="about-status__dot" />
          <span className="about-status__text">{availability}</span>
          <span className="about-status__sep">/</span>
          <span className="about-status__loc">{location}</span>
        </p>
      </div>

      {/* ── Right: the stack index ── */}
      <div className="about-stack">
        <h2 className="about-rule-title">
          <span className="about-rule-title__index">03</span>
          <span className="about-rule-title__text">Stack Index</span>
          <span className="about-rule-title__line" />
        </h2>

        {CATEGORIES.map((cat) => (
          <div key={cat} className="stack-group">
            <p className="stack-group__label">{cat}</p>

            {skills
              .filter((s) => s.cat === cat)
              .map((skill) => {
                row += 1
                return (
                  <div
                    key={skill.name}
                    className="stack-row"
                    style={{ '--i': row }}
                  >
                    <span className="stack-row__num">
                      {String(row).padStart(2, '0')}
                    </span>
                    <span className="stack-row__name">{skill.name}</span>
                    <span className="stack-row__leader" />
                    <span className="stack-row__pct" data-pct={skill.pct}>
                      0
                    </span>
                  </div>
                )
              })}
          </div>
        ))}

        <p className="stack-note">index — working depth, scored /100</p>
      </div>
    </div>
  )
}

export default About
